import React, { useState } from 'react';
import { Switch } from '../Switch/Switch';
import { useClient } from '../../hooks/useClient';
import { KeyedMutator, mutate } from 'swr';
import { useOrganizationContext } from '../../context/OrganizationContext';
import { usePopups } from '../../context/PopupContext';
import './HasOngoingInjuryToggle.scss';
import { ExternalUser } from '../../types/externalUser';

interface Props {
  user: ExternalUser;
  hasOngoingInjury: boolean | undefined;
  hasEditFeature: boolean;
  mutateUser: KeyedMutator<ExternalUser | undefined>;
  /** The id of the element that labels the value (the profile shows the label beside it). */
  labelId?: string;
}

export const HasOngoingInjuryToggle = ({ hasOngoingInjury, hasEditFeature, user, mutateUser, labelId }: Props) => {
  const { post } = useClient();
  const { organization } = useOrganizationContext();
  const [isToggling, setIsToggling] = useState(false);

  const handleToggle = async (checked: boolean) => {
    if (!organization?.id || !user?.id) return;

    setIsToggling(true);

    const result = await post<{ hasOngoingInjury: boolean }>(
      `/organizations/${organization.id}/external-users/${user.id}/toggle-ongoing-injury`,
      {
        body: { hasOngoingInjury: checked },
        successMessage: {
          summary: checked ? 'Lesión en curso activada' : 'Lesión en curso desactivada',
          detail: checked
            ? 'Se ha marcado que el usuario tiene una lesión en curso'
            : 'Se ha desmarcado que el usuario tiene una lesión en curso',
        },
        errorMessages: {
          summary: 'Error al actualizar el estado de lesión',
          defaultDetail: 'No se ha podido actualizar el estado de lesión en curso',
        },
      },
    );
    if (result) {
      mutateUser({ ...user, hasOngoingInjury: result.hasOngoingInjury }, false);
    }
    setIsToggling(false);
  };

  if (!hasEditFeature) {
    return (
      <span className='has-ongoing-injury-value'>
        <i className={hasOngoingInjury ? 'pi pi-check-circle success' : 'pi pi-times-circle error'}></i>{' '}
        {hasOngoingInjury ? 'Sí' : 'No'}
      </span>
    );
  }

  return (
    <div className='has-ongoing-injury-toggle'>
      <Switch
        id='has-ongoing-injury'
        label=''
        primeSwitchProps={{
          checked: hasOngoingInjury ?? false,
          'aria-labelledby': labelId,
          onChange: (e) => handleToggle(e.value),
          disabled: isToggling,
        }}
      />
    </div>
  );
};
